import { useCallback, useEffect, useState } from 'react';
import { useReducedMotion } from '~/shared/hooks/useReducedMotion';
import { isSSR } from '~/shared/utils/platform/utils/platform';

export interface AnimationControlState {
	/** Whether animations are currently paused */
	isPaused: boolean;
	/** Whether the user prefers reduced motion */
	prefersReducedMotion: boolean;
	/** Function to toggle pause state */
	togglePause: () => void;
	/** Function to pause animations */
	pause: () => void;
	/** Function to resume animations */
	resume: () => void;
	/** Whether animations should be disabled (either paused or user prefers reduced motion) */
	shouldDisableAnimations: boolean;
}

/**
 * Hook for controlling animations for accessibility.
 * Respects user's motion preferences and provides manual pause controls.
 * 
 * @param initiallyPaused - Whether animations should start in a paused state
 * @returns Animation control state and functions
 */
export const useAnimationControl = (initiallyPaused = false): AnimationControlState => {
	const [isPaused, setIsPaused] = useState(initiallyPaused);
	const prefersReducedMotion = useReducedMotion();

	const togglePause = useCallback(() => {
		setIsPaused(prev => !prev);
	}, []);

	const pause = useCallback(() => {
		setIsPaused(true);
	}, []);

	const resume = useCallback(() => {
		setIsPaused(false);
	}, []);

	// Handle keyboard shortcuts for accessibility
	useEffect(() => {
		if (isSSR) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			// Space or Enter to toggle pause when focused on animation controls
			if ((event.key === ' ' || event.key === 'Enter') && 
				event.target && 
				(event.target as HTMLElement).getAttribute('data-animation-control') === 'true') {
				event.preventDefault();
				togglePause();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [togglePause]);

	const shouldDisableAnimations = isPaused || prefersReducedMotion;

	return {
		isPaused,
		prefersReducedMotion,
		togglePause,
		pause,
		resume,
		shouldDisableAnimations,
	};
}; 